import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { FileBarChart, AlertCircle, TrendingUp, BookOpen, Lightbulb, Zap, Activity } from 'lucide-react';

// Animated progress bar - Removed hover movement
function AnimatedProgressBar({ value, color, delay, label }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <div ref={ref}>
      <div className="flex justify-between text-sm mb-1">
        <span>{label.split('(')[0]}</span>
        <motion.span 
          className="font-mono"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: delay + 0.5 }}
        >
          {label.match(/\d+%/)?.[0]}
        </motion.span>
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <motion.div 
          className={`h-full ${color} rounded-full`}
          initial={{ width: 0 }}
          animate={isInView ? { width: `${value}%` } : {}}
          transition={{ delay, duration: 1, ease: "easeOut" }}
        />
      </div>
    </div>
  );
}

// Animated circular progress
function CircularProgress({ value, size = 128, delay }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const circumference = 2 * Math.PI * 56;
  const strokeDashoffset = circumference - (value / 100) * circumference;

  return (
    <div ref={ref} className="relative" style={{ width: size, height: size }}>
      <svg className="w-full h-full -rotate-90">
        <circle
          cx="64"
          cy="64"
          r="56"
          fill="none"
          stroke="currentColor"
          strokeWidth="8"
          className="text-muted"
        />
        <motion.circle
          cx="64"
          cy="64"
          r="56"
          fill="none"
          stroke="currentColor"
          strokeWidth="8"
          strokeLinecap="round"
          className="text-primary"
          initial={{ strokeDasharray: circumference, strokeDashoffset: circumference }}
          animate={isInView ? { strokeDashoffset } : {}}
          transition={{ delay, duration: 1.5, ease: "easeOut" }}
        />
      </svg>
      <motion.div 
        className="absolute inset-0 flex items-center justify-center"
        initial={{ opacity: 0, scale: 0.5 }}
        animate={isInView ? { opacity: 1, scale: 1 } : {}}
        transition={{ delay: delay + 0.5 }}
      >
        <span className="text-3xl font-bold">{value}%</span>
      </motion.div>
    </div>
  );
}

// Animated insight card - REMOVED x-shift hover
function InsightCard({ text, color, delay }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: -20 }}
      animate={isInView ? { opacity: 1, x: 0 } : {}}
      transition={{ delay, duration: 0.4 }}
      className="p-3 rounded-lg bg-background/50 border border-border group"
    >
      <div className="flex items-start gap-2">
        <span className={`text-${color}`}>→</span>
        <p className={`text-sm font-mono text-${color}`}>{text}</p>
      </div>
    </motion.div>
  );
}

export default function DiagnosticPreviewSection() {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  const topicPerformance = [
    { topic: 'Kinematics', score: 85 },
    { topic: "Newton's Laws", score: 72 },
    { topic: 'Work & Energy', score: 65 },
    { topic: 'Momentum', score: 90 }
  ];

  return (
    <section 
      ref={sectionRef}
      data-testid="diagnostic-preview-section"
      className="relative py-20 md:py-32 bg-card/50"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header - MADE STATIC */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
            <FileBarChart className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary">Diagnostic Report Preview</span>
          </div>
          
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
            Short, high-signal insights.
            <br />
            <span className="text-primary">Structured intelligence.</span>
          </h2>
          
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Every report is designed to deliver maximum actionable value in minimum reading time.
          </p>
        </motion.div>

        {/* Dashboard Preview */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="relative"
        >
          {/* KEEPING GLOW EFFECT */}
          <motion.div 
            className="absolute -inset-8 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-3xl blur-3xl"
            animate={{ opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 4, repeat: Infinity }}
          />
          
          <div className="relative bg-card rounded-2xl border border-border overflow-hidden shadow-2xl">
            {/* Header Bar */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-muted/30">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-red-400" />
                <div className="w-3 h-3 rounded-full bg-amber-400" />
                <div className="w-3 h-3 rounded-full bg-green-400" />
              </div>
              <span className="text-sm font-mono text-muted-foreground flex items-center gap-2">
                <Activity className="w-4 h-4" />
                Edalyze Diagnostic Report — Physics Unit Test 3
              </span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Generated: Dec 15, 2025</span>
              </div>
            </div>

            {/* Dashboard Content */}
            <div className="p-6 lg:p-8">
              <div className="grid lg:grid-cols-3 gap-6">
                {/* Left Column - Score Overview */}
                <div className="space-y-6">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={isInView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ delay: 0.3 }}
                    className="p-6 rounded-xl bg-background border border-border"
                  >
                    <div className="text-center mb-4">
                      <p className="text-sm text-muted-foreground mb-2">Overall Score</p>
                      <CircularProgress value={78} delay={0.5} />
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-center">
                      <div>
                        <p className="text-2xl font-bold text-primary">78</p>
                        <p className="text-xs text-muted-foreground">Marks Scored</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-secondary">22</p>
                        <p className="text-xs text-muted-foreground">Marks Lost</p>
                      </div>
                    </div>
                  </motion.div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-4 rounded-xl bg-background border border-border text-center">
                      <p className="text-lg font-bold text-secondary">Q7</p>
                      <p className="text-xs text-muted-foreground">Most marks lost</p>
                    </div>
                    <div className="p-4 rounded-xl bg-background border border-border text-center">
                      <p className="text-lg font-bold">3/5</p>
                      <p className="text-xs text-muted-foreground">Numericals wrong</p>
                    </div>
                  </div>
                </div>

                {/* Middle Column - Error Analysis */}
                <div className="space-y-6">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={isInView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ delay: 0.4 }}
                    className="p-6 rounded-xl bg-background border border-border"
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <AlertCircle className="w-5 h-5 text-secondary" />
                      <h4 className="font-semibold">Error Classification</h4>
                    </div>
                    
                    <div className="space-y-4">
                      <AnimatedProgressBar 
                        value={45} 
                        color="bg-red-400" 
                        delay={0.6} 
                        label="Conceptual Errors (45%)" 
                      />
                      <AnimatedProgressBar 
                        value={30} 
                        color="bg-amber-400" 
                        delay={0.8} 
                        label="Careless Mistakes (30%)" 
                      />
                      <AnimatedProgressBar 
                        value={25} 
                        color="bg-primary" 
                        delay={1.0} 
                        label="Strategic Issues (25%)" 
                      />
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={isInView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ delay: 0.5 }}
                    className="p-6 rounded-xl bg-background border border-border"
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <TrendingUp className="w-5 h-5 text-primary" />
                      <h4 className="font-semibold">Topic Performance</h4>
                    </div>
                    
                    <div className="space-y-3">
                      {topicPerformance.map((item, index) => (
                        <AnimatedProgressBar 
                          key={index}
                          value={item.score} 
                          color="bg-primary" 
                          delay={0.6 + index * 0.15} 
                          label={`${item.topic} (${item.score}%)`} 
                        />
                      ))}
                    </div>
                  </motion.div>
                </div>

                {/* Right Column - Insights */}
                <div className="space-y-6">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={isInView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ delay: 0.6 }}
                    className="p-6 rounded-xl bg-primary/5 border border-primary/20"
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <Lightbulb className="w-5 h-5 text-primary" />
                      <h4 className="font-semibold">Key Insights</h4>
                      <Zap className="w-4 h-4 text-primary ml-auto" />
                    </div>
                    
                    <div className="space-y-3">
                      <InsightCard 
                        text="8 marks lost due to incomplete derivations in Q3 & Q7" 
                        color="primary"
                        delay={0.8}
                      />
                      <InsightCard 
                        text="Unit conversion errors in 3 numerical problems" 
                        color="secondary"
                        delay={0.9}
                      />
                      <InsightCard 
                        text="Strong conceptual understanding of momentum" 
                        color="foreground"
                        delay={1.0}
                      />
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={isInView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ delay: 0.7 }}
                    className="p-6 rounded-xl bg-background border border-border"
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <BookOpen className="w-5 h-5 text-primary" />
                      <h4 className="font-semibold">Recommendations</h4>
                    </div>
                    
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      {[
                        'Practice derivation-based problems with step marking',
                        'Review Work & Energy fundamentals',
                        'Create a unit conversion checklist'
                      ].map((item, index) => (
                        <motion.li 
                          key={index}
                          className="flex items-start gap-2"
                          initial={{ opacity: 0, x: -10 }}
                          animate={isInView ? { opacity: 1, x: 0 } : {}}
                          transition={{ delay: 1.1 + index * 0.1 }}
                        >
                          <span className="text-primary mt-0.5">•</span>
                          {item}
                        </motion.li>
                      ))}
                    </ul>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
